import { useState, useEffect } from 'react'
import {
  Button,
  Fa,
  Modal,
  TextHeader,
  showErrorMessage,
  Tooltip,
} from '@eltoro-ui/components'
import {
  Campaignservicev1OrderLine,
  Campaignservicev1Campaign,
  Creativeservicev1Creative,
  V1PoliticalFields,
  V1OrderLineStatus,
} from 'next-gen-sdk'
import {
  CollapseButton,
  OLTableUpdateRow,
  PermalinkButton,
  ReportModal,
} from 'Components'
import { useAppContext } from 'Contexts'
import { CreativeResType } from 'types'
import { OrderLineEventHistory } from './OrderLineEventHistory'
import './ExpansionHeader.scss'
import { useNavigate } from 'react-router-dom'
import { dayjs } from 'Tools/dateUtils'
import classNames from 'classnames'
import { SupportRequestModal } from 'Components/SupportRequestModal'
import { ETIcon } from 'Assets'
import { OrderLineNotes } from './OrderLineNotes'
import { IMPRESSION_MIN } from 'Components/OrderLineEditWrapper/components/shared'
import { CopyOrderlineModal } from './CopyOrderlineModal/CopyOrderlineModal'

type BaseExpansionHeaderProps = {
  icon?: JSX.Element
  name: string
  isCollapsed: boolean
  handleCollapse: () => void
  collapseIconName?: string
  setRefreshOL?: React.Dispatch<React.SetStateAction<boolean>>
  handleUpdateRow?: OLTableUpdateRow
}

type OrderLineExpansionHeader = BaseExpansionHeaderProps & {
  type: 'orderLine'
  orderline: Campaignservicev1OrderLine
  campaign?: never
}

type CampaignExpansionHeader = BaseExpansionHeaderProps & {
  type: 'campaign'
  campaign: Campaignservicev1Campaign
  orderline?: never
}

export const ExpansionHeader = ({
  type,
  icon,
  name,
  isCollapsed,
  handleCollapse,
  orderline,
  campaign,
  collapseIconName,
  setRefreshOL,
  handleUpdateRow,
}: OrderLineExpansionHeader | CampaignExpansionHeader) => {
  const {
    currentOrg,
    roles,
    campaignServiceApi,
    isOnHold,
    isAdmin,
  } = useAppContext()
  // Modal booleans
  const [showReport, setShowReport] = useState(false)
  const [cloneModal, setCloneModal] = useState(false)
  const [supportModal, setSupportModal] = useState(false)
  // LockModal
  const [lockModal, setLockModal] = useState(false)

  const navigate = useNavigate()

  const hasDeployed =
    (orderline?.status &&
      ([
        'ORDERLINE_STATUS_DEPLOYED',
        'ORDERLINE_STATUS_SERVING',
        'ORDERLINE_STATUS_COMPLETED',
        'ORDERLINE_STATUS_ARCHIVED',
        'ORDERLINE_STATUS_PAUSED',
        'ORDERLINE_STATUS_ERRORED_AND_SERVING',
        'ORDERLINE_STATUS_ERRORED_AND_DEPLOYED',
      ] as V1OrderLineStatus[]).includes(orderline.status)) ||
    !!orderline?.firstDeployTime

  const pastStartDate =
    orderline?.startTime &&
    orderline?.startTime.getTime() <= new Date().getTime()
  const isArchived =
    orderline?.state === 'ORDERLINE_STATE_ARCHIVED' ||
    orderline?.status === 'ORDERLINE_STATUS_ARCHIVED'

  const isLocked = orderline?.locked || false

  const query = currentOrg?.id ? `?org_id=${currentOrg?.id}` : ''

  const handleAddOrderLine = async () => {
    let political: boolean = !!campaign?.political
    const today = dayjs().tz().format('YYYY-MM-DD')
    const now = dayjs().tz().add(1, 'day').startOf('day').utc().toDate()
    const nowPlusSeven = dayjs().tz().add(8, 'days').endOf('day').utc().toDate()
    if (!campaignServiceApi || !campaign?.orgId) return
    campaignServiceApi
      .advertisingPlatformServiceCreateOrderLine(
        campaign.orgId,
        {
          name: `${campaign?.name}_Draft_${today}`,
          startTime: now,
          endTime: nowPlusSeven,
          impressions: currentOrg?.minimumImpressions || IMPRESSION_MIN,
          political,
        },
        campaign?.id,
      )
      .then(() => {
        navigate(`/campaigns/edit/${campaign?.id}/settings${query}`)
      })
  }
  const handleLockOrderline = () => {
    if (
      campaignServiceApi &&
      orderline?.id &&
      orderline.orgId &&
      roles &&
      roles.includes('nextgen_admin')
    ) {
      campaignServiceApi
        .advertisingPlatformServiceLockOrderLine(orderline?.id, {
          orgId: orderline.orgId,
        })
        .then(() => {
          setLockModal(false)
          if (handleUpdateRow) handleUpdateRow({ ...orderline, locked: true })
          if (setRefreshOL) setRefreshOL(true)
        })
        .catch((err) => {
          showErrorMessage('Error locking order line', err?.body?.message || '')
        })
    }
  }
  const handleUnlockOrderline = () => {
    if (
      campaignServiceApi &&
      orderline?.id &&
      orderline?.orgId &&
      roles &&
      roles.includes('nextgen_admin')
    ) {
      campaignServiceApi
        .advertisingPlatformServiceUnlockOrderLine(orderline?.id, {
          orgId: orderline.orgId,
        })
        .then(() => {
          setLockModal(false)
          if (handleUpdateRow) handleUpdateRow({ ...orderline, locked: false })
          if (setRefreshOL) setRefreshOL(true)
        })
        .catch((err) => {
          showErrorMessage(
            'Error unlocking order line',
            err?.body?.message || '',
          )
        })
    }
  }

  return (
    <div className="ExpansionHeader relative">
      <CollapseButton
        classNameButton="ExpansionHeader__collapse-button desktop:!w-full justify-end !pb-0"
        className="desktop:!w-auto"
        isCollapsed={isCollapsed}
        handleClick={handleCollapse}
        iconName={collapseIconName}
      />

      <div className="ExpansionHeader__content laptopsmall:flex-wrap laptopsmall:gap-0 laptopsmall:justify-end desktop:items-center absolute top-3 right-9 flex flex-1 justify-between gap-4">
        <div className="OrderLine__name-icon flex flex-1 items-center gap-2">
          <div className="flex flex-1 items-center justify-start gap-1">
            {icon}
            {type === 'orderLine' && orderline?.political && (
              <i
                className="w-18"
                data-tooltip="right"
                aria-label="This is a political order line"
              >
                <ETIcon icon="advocacy" height="15" width="15" />
              </i>
            )}
            <TextHeader
              className="ExpansionHeader__text pointer-events-none truncate leading-snug"
              type={4}
            >
              {name}
            </TextHeader>
            <div className="OrderLine__icons-no-text gap-5">
              {type === 'orderLine' && orderline?.id && (
                <button
                  className={`LockIcon__tooltip Button ${
                    !isAdmin && 'pointer-events-none'
                  }`}
                  onClick={() => {
                    if (isAdmin) {
                      setLockModal(true)
                    }
                  }}
                  data-tooltip="bottom"
                  aria-label={
                    orderline?.locked
                      ? 'This order line is locked. Please submit a support request if you need to make any changes.'
                      : 'Click to lock this order line'
                  }
                >
                  <Fa
                    icon={orderline?.locked ? 'lock' : 'lock-open'}
                    className={`text-primary scale-75 ${
                      roles &&
                      !roles.includes('nextgen_admin') &&
                      !isOnHold &&
                      'pointer-events-none'
                    }`}
                    size={1}
                  />
                </button>
              )}
              {(() => {
                if (type === 'orderLine' && orderline?.id && orderline?.orgId)
                  return (
                    <PermalinkButton
                      url={`/campaigns/dashboard/order-lines/${orderline.id}?org_id=${orderline.orgId}`}
                      className="desktop:mr-auto"
                    />
                  )
                if (type === 'campaign' && campaign?.id && campaign?.orgId)
                  return (
                    <PermalinkButton
                      url={`/campaigns/dashboard/${campaign.id}?org_id=${campaign.orgId}`}
                      className="desktop:mr-auto"
                    />
                  )
              })()}
            </div>
          </div>
        </div>
        <div className="OrderLine__icons items-center gap-4">
          <div className="OrderLine__icons-no-text">
            {orderline && (
              <OrderLineNotes
                orderLine={orderline}
                rejected={orderline.status === 'ORDERLINE_STATUS_REJECTED'}
              />
            )}
            {orderline && <OrderLineEventHistory orderline={orderline} />}
          </div>
          <div className="OrderLine__icons-with-text flex flex-wrap items-center gap-4">
            {type === 'campaign' &&
              roles &&
              !roles.includes('nextgen_read_only') &&
              !isOnHold && (
                <Button
                  className="AddOrderLineButton"
                  iconLeft={<Fa icon="add" size={1} />}
                  ariaLabel="Add Order Line"
                  dataTooltip="bottom"
                  onClick={handleAddOrderLine}
                >
                  Add Order Line
                </Button>
              )}
            {roles &&
              !roles.includes('nextgen_read_only') &&
              !isOnHold &&
              type === 'orderLine' && (
                <Tooltip
                  position="left"
                  content={`${
                    isArchived
                      ? 'Unable to copy this order line because it is archived.'
                      : isLocked && !isAdmin
                      ? 'Unable to copy this order line because it is locked. Please submit a support request if you need further assistance.'
                      : ''
                  }`}
                  classNameTooltip="w-[10rem]"
                  className="focus:rounded-sm"
                >
                  <Button
                    className="CopyButton"
                    iconLeft={<Fa icon="copy" size={1} />}
                    ariaLabel={
                      !isArchived && !(isLocked && !isAdmin)
                        ? 'Copy order line'
                        : undefined
                    }
                    dataTooltip={
                      !isArchived && !(isLocked && !isAdmin)
                        ? 'left'
                        : undefined
                    }
                    onClick={() => setCloneModal(true)}
                    disabled={isArchived || (isLocked && !isAdmin)}
                  >
                    Copy order line
                  </Button>
                </Tooltip>
              )}

            {type === 'orderLine' && (
              <>
                <Button
                  disabled={!hasDeployed || !pastStartDate}
                  className="PerformanceButton"
                  iconLeft={<Fa icon="chart-line" size={1} />}
                  onClick={() => setShowReport(true)}
                  ariaLabel={`${
                    !hasDeployed || !pastStartDate
                      ? 'Performance Report unavailable'
                      : 'Click to see Performance Button'
                  }`}
                  dataTooltip="left"
                >
                  Performance Report
                </Button>
              </>
            )}
            <Button
              iconLeft={<Fa icon="question" size={1} />}
              onClick={() => setSupportModal(true)}
              ariaLabel="Support Request"
              dataTooltip="left"
            >
              Support Request
            </Button>
          </div>
        </div>
      </div>
      {type === 'orderLine' && orderline && showReport && (
        <ReportModal
          orderline={orderline}
          closeModal={() => setShowReport(false)}
        />
      )}
      {lockModal && (
        <Modal
          offClick={() => setLockModal(false)}
          className={classNames({ 'max-w-[32rem]': lockModal })}
        >
          <div className="LockOrderLine__modal flex flex-col gap-1">
            <TextHeader className="flex flex-col" type={3}>
              <span>
                {orderline?.locked ? 'Unlock' : 'Lock'} this order line?
              </span>
              <span className="text-l font-normal normal-case leading-none">
                {name}
              </span>
            </TextHeader>

            <div className="ButtonGroup mb-2 flex justify-between pt-8">
              <Button
                onClick={() => {
                  setLockModal(false)
                }}
              >
                Cancel
              </Button>
              {orderline?.locked ? (
                <Button onClick={handleUnlockOrderline}>Unlock!</Button>
              ) : (
                <Button onClick={handleLockOrderline}>Lock!</Button>
              )}
            </div>
          </div>
        </Modal>
      )}
      {type === 'orderLine' && orderline && (
        <CopyOrderlineModal
          orderline={orderline}
          isOpen={cloneModal}
          onClose={() => setCloneModal(false)}
        />
      )}
      {supportModal && (
        <SupportRequestModal
          handleClose={() => setSupportModal(false)}
          orderLine={orderline}
          campaign={campaign}
        />
      )}
    </div>
  )
}
